import { useRef, useState } from 'react';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { postProjectUpdate } from '../../lib/projectUpdates';

interface ProjectUpdateComposerProps {
  projectId: string;
  authorId: string;
  disabled?: boolean;
  allowAttachments?: boolean;
  onPosted: () => void;
}

export function ProjectUpdateComposer({
  projectId,
  authorId,
  disabled,
  allowAttachments = true,
  onPosted,
}: ProjectUpdateComposerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function clearFile() {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handlePost(e: React.FormEvent) {
    e.preventDefault();
    if (disabled || posting) return;
    if (!message.trim() && !file) {
      setError('Please enter a message or attach a file.');
      return;
    }

    setPosting(true);
    setError(null);

    try {
      await postProjectUpdate({ projectId, authorId, message, file });
      setMessage('');
      clearFile();
      onPosted();
    } catch (err) {
      setError(formatSupabaseError(err, file ? 'attachment' : 'crm'));
    } finally {
      setPosting(false);
    }
  }

  return (
    <form className="project-composer" onSubmit={(e) => void handlePost(e)}>
      {error && <p className="field-hint project-composer__error">{error}</p>}
      <textarea
        className="field-input project-composer__input"
        rows={3}
        placeholder="Enter an update"
        value={message}
        disabled={disabled || posting}
        onChange={(e) => setMessage(e.target.value)}
      />
      {file && (
        <div className="project-composer__file-row">
          <span className="project-composer__file">📎 {file.name}</span>
          <button
            type="button"
            className="btn-ghost btn-sm project-composer__remove"
            disabled={posting}
            onClick={clearFile}
          >
            Remove
          </button>
        </div>
      )}
      <div className="project-composer__actions">
        {allowAttachments && (
          <>
            <input
              ref={fileRef}
              type="file"
              className="account-logo-upload__input"
              disabled={disabled || posting}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={disabled || posting}
              onClick={() => fileRef.current?.click()}
            >
              Attach file
            </button>
          </>
        )}
        <button type="submit" className="btn-primary btn-sm" disabled={disabled || posting}>
          {posting ? 'Posting…' : 'Post update'}
        </button>
      </div>
    </form>
  );
}
